<ng-container *transloco="let t">
  <h1 mat-dialog-title>
    <div class="flex align-center">
    <mat-icon class="material-symbols-outlined">notifications</mat-icon>
    {{ t('notifications.detail.title') }}
    </div>
  </h1>

  <mat-dialog-content>
    <mat-card class="margin-xs-bottom">
      <mat-card-header>
        <div class="flex space-between width-100" [ngClass]="{ 'wrap': isMobile }">
          <div class="flex align-center">
            <mat-card-title>{{ notification.title }}</mat-card-title>
            <div class="flex align-center no-gap">
              <mat-icon class="material-symbols-outlined mr-1" [class]="getPriorityClass(notification.notificationType?.priority || 2)">
                {{ getPriorityIcon(notification.notificationType?.priority || 2) }}
              </mat-icon>
              <span [class]="getPriorityClass(notification.notificationType?.priority || 2)">
                {{ priorityLabels[notification.notificationType?.priority || 2] }}
              </span>
            </div>
          </div>
          <mat-chip-set>
            <mat-chip>
              {{ getStatusLabel(notification.status) }}
            </mat-chip>
            @if (notification.requiresAction) {
              <mat-chip style="background-color: var(--bg-accent);" [matTooltip]="t('notifications.detail.requiresAction.hint')">
                {{ t('notifications.detail.requiresAction') }}
              </mat-chip>
            }
          </mat-chip-set>
        </div>
      </mat-card-header>
    </mat-card>

    <mat-card class="margin-xs-bottom">
      <mat-card-header>
        <mat-card-title>{{ t('notifications.detail.message') }}</mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <p class="message-content">{{ notification.message }}</p>
      </mat-card-content>
    </mat-card>

    <mat-card class="margin-xs-bottom">
      <mat-card-header>
        <mat-card-title>{{ t('notifications.detail.details') }}</mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="grid two-columns">
          <div class="info-item">
            <div class="item-label flex align-center">
              <mat-icon class="material-symbols-outlined">{{ getTargetIcon() }}</mat-icon>
              {{ t('notifications.detail.target') }}:
            </div>
            <span>{{ getTargetDisplay() }}</span>
          </div>

          <div class="info-item">
            <div class="item-label flex align-center">
              <mat-icon class="material-symbols-outlined">category</mat-icon>
              {{ t('notifications.detail.type') }}:
            </div>
            <div class="type-badge" [style.background-color]="userNotificationService.getTypeBadgeBackgroundColor(notification)">
              <mat-icon [class]="userNotificationService.inAppTypeMatIconClasses">
                {{ userNotificationService.getTypeIconName(notification) }}
              </mat-icon>
              <span>{{ notification.notificationType?.name }}</span>
            </div>
          </div>

          <div class="info-item">
            <div class="item-label flex align-center">
              <mat-icon class="material-symbols-outlined">event</mat-icon>
              {{ t('notifications.detail.created') }}:
            </div>
            <span>{{ notification.createdAt | date:'medium' }}</span>
          </div>

          @if (notification.createdBy) {
            <div class="info-item">
              <div class="item-label flex align-center">
                <mat-icon class="material-symbols-outlined">person</mat-icon>
                {{ t('notifications.detail.createdBy') }}:
              </div>
              <span>{{ notification.createdBy.firstName }} {{ notification.createdBy.lastName }}</span>
            </div>
            <div class="info-item">
                <div class="item-label flex align-center">
                  <mat-icon class="material-symbols-outlined">phone</mat-icon>
                  {{ t('notifications.detail.createdBy.phone') }}:
                </div>
                <a [href]="`tel:${notification.createdBy.phoneNumber}`">{{ notification.createdBy.phoneNumber }}</a>
              </div>
              <div class="info-item">
                <div class="item-label flex align-center">
                  <mat-icon class="material-symbols-outlined">email</mat-icon>
                  {{ t('notifications.detail.createdBy.email') }}:
                </div>
                <a [href]="`mailto:${notification.createdBy.email}`">{{ notification.createdBy.email }}</a>
              </div>
          }

          @if (notification.expiresAt) {
            <div class="info-item">
              <div class="item-label flex align-center">
                <mat-icon class="material-symbols-outlined">schedule</mat-icon>
                {{ t('notifications.detail.expires') }}:
              </div>
              <span [class.text-error]="isExpired()">
                {{ notification.expiresAt | date:'medium' }}
                @if (isExpired()) {
                  <mat-icon class="material-symbols-outlined inline-icon text-error">warning</mat-icon>
                }
              </span>
            </div>
          }

          @if (notification.processId) {
            <div class="info-item">
              <div class="item-label flex align-center">
                <mat-icon class="material-symbols-outlined">account_tree</mat-icon>
                {{ t('notifications.detail.process') }}:
              </div>
              <span>#{{ notification.processId }}</span>
            </div>
        }

          <div class="info-item">
            <div class="item-label flex align-center">
              <mat-icon class="material-symbols-outlined">link</mat-icon>
              {{ t('notifications.detail.assets') }}:
            </div>
            <span style="white-space: pre-line">{{ articleText }}</span>
          </div>

          @if (detail?.processedBy) {
            <div class="info-item">
              <div class="item-label flex align-center">
                <mat-icon class="material-symbols-outlined">manage_accounts</mat-icon>
                {{ t('notifications.detail.processedBy') }}:
              </div>
              <span>{{ processedByName }} {{ t('notifications.detail.processedBy.on') }} {{ detail.processedBy.at | date:'medium' }}</span>
            </div>
          }
        </div>
      </mat-card-content>
    </mat-card>

    @if (hasEmployeeContext) {
      <mat-card class="margin-xs-bottom">
        <mat-card-header>
          <mat-card-title>{{ t('notifications.detail.employee') }}</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <div class="grid two-columns">
            <div class="info-item">
              <div class="item-label flex align-center">
                <mat-icon class="material-symbols-outlined">badge</mat-icon>
                {{ t('notifications.detail.employee.name') }}:
              </div>
              <span>{{ employeeName }}</span>
            </div>
            <div class="info-item">
              <div class="item-label flex align-center">
                <mat-icon class="material-symbols-outlined">directions_boat</mat-icon>
                {{ t('notifications.detail.employee.ship') }}:
              </div>
              <span>{{ employeeShip }}</span>
            </div>
            <div class="info-item">
              <div class="item-label flex align-center">
                <mat-icon class="material-symbols-outlined">apartment</mat-icon>
                {{ t('notifications.detail.employee.shipOperator') }}:
              </div>
              <span>{{ employeeShipOperator }}</span>
            </div>
          </div>
        </mat-card-content>
      </mat-card>
    }

    @if (detail?.reportComment) {
      <mat-card class="margin-xs-bottom">
        <mat-card-header>
          <mat-card-title>{{ t('notifications.detail.reportComment') }}</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <p class="notes-content">{{ reportComment }}</p>
        </mat-card-content>
      </mat-card>
    }

    @if (defectReportResult; as outcome) {
      <mat-card class="margin-xs-bottom">
        <mat-card-header>
          <mat-card-title>{{ t('notifications.detail.defectReportResult') }}</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <div class="info-item">
            <div class="item-label flex align-center">
              <mat-icon class="material-symbols-outlined">{{ outcome.result === 'approved' ? 'check_circle' : 'cancel' }}</mat-icon>
              {{ outcome.result === 'approved' ? t('notifications.detail.defectReportResult.approved') : t('notifications.detail.defectReportResult.rejected') }}
            </div>
          </div>
          @if (outcome.rejectionReason) {
            <div class="item-label">{{ t('notifications.detail.defectReportResult.reason') }}:</div>
            <p class="notes-content">{{ outcome.rejectionReason }}</p>
          }
        </mat-card-content>
      </mat-card>
    }

    @if (detail?.attachments?.length) {
      <mat-card class="margin-xs-bottom">
        <mat-card-header>
          <mat-card-title>{{ t('notifications.detail.attachments') }}</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <div class="flex wrap">
            @for (attachment of detail.attachments; track attachmentKey(attachment)) {
              @if (attachmentUrls[attachmentKey(attachment)]) {
                <a [href]="attachmentUrls[attachmentKey(attachment)]" target="_blank" rel="noopener">
                  <img
                    class="notification-attachment"
                    [src]="attachmentUrls[attachmentKey(attachment)]"
                    [alt]="attachment.fileName ?? attachment.fieldName"
                  />
                </a>
              }
            }
          </div>
        </mat-card-content>
      </mat-card>
    }

    @if (detailFailed) {
      <p class="text-error">{{ t('notifications.detail.load_failed') }}</p>
    }

    @if (isInventoryDeclarationNotification()) {
      <app-inventory-declaration-notification-panel
        [notification]="notification"
        (resolved)="onInventoryDeclarationResolved()"
      />
    }

    @if (isDocumentReviewNotification()) {
      <app-document-review-notification-panel
        [notification]="notification"
        (resolved)="onDocumentReviewResolved()"
      />
    }

    @if (isOrderInquiryNotification()) {
      <app-order-inquiry-notification-panel [notification]="notification" (answered)="onOrderInquiryAnswered()" />
    }

    @if (isSurveyNotification()) {
      <!-- Nachladen, damit Radio- und Checkbox-Module nicht im eager Bundle der Kopfzeilen-Glocke landen. -->
      @defer (on immediate) {
        <app-survey-notification-panel [notification]="notification" (answered)="onSurveyAnswered()" />
      }
    }

    @if (isDocumentExpiryNotification()) {
      <!-- Nachladen, damit der Datepicker nicht im eager Bundle der Kopfzeilen-Glocke landet. -->
      @defer (on immediate) {
        <app-document-expiry-notification-panel
          [notification]="notification"
          (uploaded)="onDocumentExpiryUploaded()"
        />
      }
    }

    @if (notification.notes) {
      <mat-card class="margin-xs-bottom">
        <mat-card-header>
          <mat-card-title>{{ t('notifications.detail.notes') }}</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <p class="notes-content">{{ notification.notes }}</p>
          @if (notification.notesModifiedByUserIds && notification.notesModifiedByUserIds.length > 0) {
            <small class="text-muted">{{ t('notifications.detail.modifiedBy') }}: {{ getNotesModifiedByNames() }}</small>
          }
        </mat-card-content>
      </mat-card>
    }

    <!-- @if (notification.metadata && objectKeys(notification.metadata).length > 0) {
      <mat-card class="margin-xs-bottom">
        <mat-card-header>
          <mat-card-title>{{ t('notifications.detail.metadata') }}</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <pre class="metadata-content">{{ notification.metadata | json }}</pre>
        </mat-card-content>
      </mat-card>
    } -->

    <mat-card>
      <mat-card-header>
        <mat-card-title>{{ t('notifications.detail.timeline') }}</mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="flex column">
          <div class="timestamp-item flex align-center">
            <mat-icon class="material-symbols-outlined">add_circle</mat-icon>
            <span>{{ t('notifications.detail.created') }}: {{ notification.createdAt | date:'medium' }}</span>
          </div>
          @if (notification.readAt) {
            <div class="timestamp-item flex align-center">
              <mat-icon class="material-symbols-outlined">visibility</mat-icon>
              <span>{{ t('notifications.detail.read') }}: {{ notification.readAt | date:'medium' }}</span>
            </div>
          }
          @if (notification.acceptedAt) {
            <div class="timestamp-item flex align-center">
              <mat-icon class="material-symbols-outlined">check_circle</mat-icon>
              <div class="flex column">
                <span>{{ t('notifications.detail.accepted') }}: {{ notification.acceptedAt | date:'medium' }}</span>
                @if (notification.acceptedBy) {
                  <small class="text-muted">{{ t('notifications.detail.by') }} {{ notification.acceptedBy.firstName }} {{ notification.acceptedBy.lastName }}</small>
                }
              </div>
            </div>
          }
          @if (notification.rejectedAt) {
            <div class="timestamp-item flex align-center">
              <mat-icon class="material-symbols-outlined">cancel</mat-icon>
              <div class="flex column">
                <span>{{ t('notifications.detail.rejected') }}: {{ notification.rejectedAt | date:'medium' }}</span>
                @if (notification.rejectedBy) {
                  <small class="text-muted">{{ t('notifications.detail.by') }} {{ notification.rejectedBy.firstName }} {{ notification.rejectedBy.lastName }}</small>
                }
              </div>
            </div>
          }
        </div>
      </mat-card-content>
    </mat-card>
  </mat-dialog-content>

  <mat-dialog-actions class="flex no-gap justify-end">
    <span class="flex-grow-1"></span>
    @if (canArchive) {
      <button mat-button (click)="archive()">{{ t('notifications.detail.archive') }}</button>
    } @else if (notification.status !== 'archived') {
      <!-- Process-bound notifications leave the inbox only when the process resolves them. -->
      <button mat-button disabled disabledInteractive [matTooltip]="t('notifications.detail.archive_blocked')">
        <mat-icon class="material-symbols-outlined">lock</mat-icon>
        {{ t('notifications.detail.archive') }}
      </button>
    }
    <button mat-button mat-dialog-close>{{ t('general.close') }}</button>
    @if (notification.requiresAction) {
      @if (isInventoryDeclarationNotification() || isDocumentReviewNotification()) {
        <!-- submit handled inside notification panel -->
      } @else if (notification.processId) {
        <button mat-raised-button color="primary" (click)="resumeProcess(notification.processId)">{{ t('views.psa.edit_problem') }}</button>
      }
      @else if (notification.actionUrl) {
        <a mat-raised-button color="primary" [href]="notification.actionUrl" target="_blank">{{ t('views.psa.edit_problem') }}</a>
      }
    } @else if (notification.actionUrl) {
      <a mat-raised-button color="primary" [href]="notification.actionUrl" target="_blank">{{ t('notifications.detail.view_link') }}</a>
    }
  </mat-dialog-actions>
</ng-container>
